import React, { FC } from 'react';
import { BasicContainer, Loader } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import { PlanTermEnum } from '@minecraft.graphql-operations';
import { CompletePurchaseLoader } from './CompletePurchaseLoader';
import { CheckoutProps } from './types';
import { Checkout as CheckoutV1 } from './Checkout';
import { CheckoutV2 } from './CheckoutV2';
import { SinglePageUpgradeModalExperimentGroups } from '../enums/experimentGroups';
import { ThreeDActionType } from '../ThreeDSecureFlow';

export const CheckoutWrapper: FC<CheckoutProps> = ({
  onNext,
  currentPurchase,
  onCompletePurchase,
  currencyCode,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  completePurchaseLoading,
  completePurchaseError,
  wallet,
  walletLoading,
  selectedPaymentOption,
  onSelectPaymentOption,
  addPaymentError,
  isAnnual,
  plans,
  plansError,
  plansLoading,
  setIsAnnual,
  selectedPlan,
  setSelectedPlan,
  countryCode,
  onBack,
  startingStep,
  onAddPaymentOption,
  setShowAddPaymentMethod = () => null,
  showAddPaymentMethod,
  addPaymentLoading,
  threeDActionContext,
  onThreeDActionError,
  isModal,
  getTitle = () => null,
  paywallLocation,
  currentPlanCode,
  enableAnnualRenewal,
  isWithinTrialPeriod,
  previewMediaPurchaseLoading,
  previewMediaPurchase,
  closeBillingPopup,
  onPaymentCompleteRedirect,
  isAnnualToggleLocked,
  currentProfile,
}) => {
  const { t } = useTranslation();
  const isSinglePageUpgradeModal =
    useFeatureExperiment(FEATURE_FLAGS.SINGLE_PAGE_UPGRADE_MODAL) === SinglePageUpgradeModalExperimentGroups.Variant;
  const Checkout = isModal && isSinglePageUpgradeModal ? CheckoutV2 : CheckoutV1;

  return (
    <BasicContainer mx={isModal ? '0' : '2'} w="full">
      <Loader
        loading={completePurchaseLoading || threeDActionContext?.actionType === ThreeDActionType.PURCHASE}
        LoaderComponent={<CompletePurchaseLoader />}
        error={plansError && t('common:globalError.genericUnexpected')}
      >
        <Checkout
          onNext={onNext}
          currentPurchase={currentPurchase}
          onCompletePurchase={onCompletePurchase}
          currencyCode={currencyCode}
          onApplyCoupon={onApplyCoupon}
          couponLoading={couponLoading}
          applyCouponError={applyCouponError}
          onRemoveCoupon={onRemoveCoupon}
          completePurchaseLoading={completePurchaseLoading}
          completePurchaseError={completePurchaseError}
          wallet={wallet}
          walletLoading={walletLoading}
          selectedPaymentOption={selectedPaymentOption}
          onSelectPaymentOption={onSelectPaymentOption}
          addPaymentError={addPaymentError}
          isAnnual={isAnnual}
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          setIsAnnual={setIsAnnual}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          countryCode={countryCode}
          onBack={onBack}
          startingStep={startingStep}
          onAddPaymentOption={onAddPaymentOption}
          setShowAddPaymentMethod={setShowAddPaymentMethod}
          showAddPaymentMethod={showAddPaymentMethod}
          addPaymentLoading={addPaymentLoading}
          threeDActionContext={threeDActionContext}
          onThreeDActionError={onThreeDActionError}
          isModal={isModal}
          getTitle={getTitle}
          paywallLocation={paywallLocation}
          currentPlanCode={currentPlanCode}
          enableAnnualRenewal={enableAnnualRenewal}
          isWithinTrialPeriod={isWithinTrialPeriod}
          previewMediaPurchaseLoading={previewMediaPurchaseLoading}
          previewMediaPurchase={previewMediaPurchase}
          closeBillingPopup={closeBillingPopup}
          onPaymentCompleteRedirect={onPaymentCompleteRedirect}
          selectedPlanTerm={selectedPlan?.term?.code === PlanTermEnum.Annual ? 'annual' : 'monthly'}
          isAnnualToggleLocked={isAnnualToggleLocked}
          currentProfile={currentProfile}
        />
      </Loader>
    </BasicContainer>
  );
};
